/**
 * After a test call, gather what the AI builder needs to diagnose it: for EVERY
 * leg of the session, the transcript, the function calls/results, and a trimmed
 * invocation log.
 *
 * A call can span several legs: if the tested agent runs a `transfer_agent`
 * handover, the conversation continues as a NEW call under a DIFFERENT agent
 * (e.g. Reception → Sales). The builder must see all of them to diagnose a
 * transfer, so we (1) DISCOVER the tested agent's leg — the seed — from its own
 * call list (matched by instanceId/time during the live test), then (2) EXPAND
 * to the whole linked tree via `/calls/{id}/linked`, which walks the durable
 * `parentId` chain (root + all descendants, any agent). parentId is used for the
 * linking because it survives listener teardown — unlike instanceId — so the
 * same path also works for historical calls.
 *
 * Transcript logs are batched and the invocation log is persisted at call
 * *shutdown*, which lags the hangup, so we poll the linked tree until every leg
 * has `endedAt` (a transfer ends the parent leg early but the child runs on) and
 * retry the log fetch if it's still settling. The invocation log can be hundreds
 * of entries, so it is filtered to warnings/errors and capped to keep the
 * builder's context small.
 */
import {
  listCallsForAgent,
  getLinkedCalls,
  getCallTransactionLog,
  getCallInvocationLog,
} from '../../api/agent';

const POLL_MS = 1500;
const POLL_TRIES = 20;            // ~30s cap; exits early once the call has ended
const SINCE_SLACK_MS = 30000;     // tolerate client/server clock skew in the time fallback
const INVOCATION_CAP = 50;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const parseData = (d) => { try { return JSON.parse(d); } catch { return d; } };

async function listCalls(agentId) {
  try {
    const data = await listCallsForAgent(agentId, { limit: 15 });
    return Array.isArray(data?.calls) ? data.calls : [];
  } catch {
    return [];
  }
}

/** Choose the test's call: exact listener match first, else most recent since test start. */
function pickCandidate(calls, listenerId, since) {
  let pool = listenerId ? calls.filter((c) => c.instanceId && c.instanceId === listenerId) : [];
  if (!pool.length && since) {
    pool = calls.filter((c) => {
      const t = new Date(c.startedAt || c.createdAt || 0).getTime();
      return Number.isFinite(t) && t >= since - SINCE_SLACK_MS;
    });
  }
  if (!pool.length) return null;
  // The list is createdAt DESC; prefer the root leg (caller↔agent) over child/consult legs.
  return pool.find((c) => !c.parentId) || pool[0];
}

async function findTestCall(agentId, listenerId, since) {
  let latest = null;
  for (let i = 0; i < POLL_TRIES; i += 1) {
    const calls = await listCalls(agentId);
    const cand = pickCandidate(calls, listenerId, since);
    if (cand) {
      latest = cand;
      if (cand.endedAt) return cand;        // ended — logs are flushing
    }
    if (i < POLL_TRIES - 1) await sleep(POLL_MS);
  }
  return latest;                            // in-progress or not found
}

async function listLinked(callId) {
  try {
    const data = await getLinkedCalls(callId, { limit: 25 });
    return Array.isArray(data?.calls) ? data.calls : [];
  } catch {
    return [];
  }
}

/**
 * Expand a seed call to its whole linked tree (durable parentId chain) and poll
 * until every leg has ended (logs flushing), capped. A transfer ends the parent
 * leg at handover but the child leg runs until hangup, so we wait on the tree —
 * not just the seed — for the session to settle.
 */
async function waitForLinkedTree(seedId) {
  let legs = [];
  for (let i = 0; i < POLL_TRIES; i += 1) {
    const cur = await listLinked(seedId);
    if (cur.length) {
      legs = cur;
      if (legs.every((c) => c.endedAt)) return legs;   // fully settled
    }
    if (i < POLL_TRIES - 1) await sleep(POLL_MS);
  }
  return legs;                                          // best-effort (a leg may still be live)
}

/** Root leg (no parent) first, then in start order — the order the call actually flowed. */
function orderLegs(legs) {
  return [...legs].sort((a, b) => {
    const ar = a.parentId ? 1 : 0;
    const br = b.parentId ? 1 : 0;
    if (ar !== br) return ar - br;
    const at = new Date(a.startedAt || a.createdAt || 0).getTime();
    const bt = new Date(b.startedAt || b.createdAt || 0).getTime();
    return at - bt;
  });
}

function transcriptOf(logs) {
  const TEXT = new Set(['agent', 'user', 'goodbye', 'hangup', 'inject']);
  return logs.filter((l) => TEXT.has(l.type)).map((l) => ({ role: l.type, text: parseData(l.data) }));
}

function functionsOf(logs) {
  const FN = new Set(['function_calls', 'function_results', 'rest_callout']);
  return logs.filter((l) => FN.has(l.type)).map((l) => ({ type: l.type, data: parseData(l.data) }));
}

/** Flatten invocation-log records, keep warnings/errors (pino level >= 40), cap. */
function invocationOf(records) {
  const entries = [];
  (records || []).forEach((rec) => {
    const log = rec?.log;
    if (Array.isArray(log)) entries.push(...log);
    else if (log) entries.push(log);
  });
  const notable = entries.filter((e) => typeof e?.level === 'number' && e.level >= 40);
  const picked = (notable.length ? notable : entries).slice(-INVOCATION_CAP).map((e) => ({
    level: e?.level,
    msg: e?.msg,
    error: e?.err?.message || e?.error?.message || e?.error || undefined,
  }));
  return { total: entries.length, notable: notable.length, entries: picked };
}

/** Fetch + trim one leg's transcript, function calls and invocation log. */
async function fetchLegData(call, agentIdFallback) {
  // The batched transcript + invocation log can lag a moment behind the call
  // ending, so retry once if nothing has landed yet.
  let logs = [];
  let invocation = [];
  for (let attempt = 0; attempt < 2; attempt += 1) {
    // eslint-disable-next-line no-await-in-loop
    [logs, invocation] = await Promise.all([
      getCallTransactionLog(call.id).catch(() => []),
      getCallInvocationLog(call.id).catch(() => []),
    ]);
    logs = Array.isArray(logs) ? logs : [];
    invocation = Array.isArray(invocation) ? invocation : [];
    if (logs.length || invocation.length || attempt === 1 || !call.endedAt) break;
    // eslint-disable-next-line no-await-in-loop
    await sleep(POLL_MS);
  }
  return {
    callId: call.id,
    agentId: call.agentId || agentIdFallback || null,
    agentName: call.agentName || null,
    agentLabel: call.agentLabel || null,
    parentId: call.parentId || null,
    modelName: call.modelName || null,
    status: call.status || (call.endedAt ? 'ended' : 'in progress'),
    ended: !!call.endedAt,
    transcript: transcriptOf(logs),
    functions: functionsOf(logs),
    invocationLog: invocationOf(invocation),
  };
}

/**
 * @param {string} agentId      the tested agent (used for the fallback lookup)
 * @param {string} listenerId   the listener/instance id — the key that links all legs
 * @param {number} [since]      ms timestamp when the test started (time fallback)
 * @returns {Promise<{ok:boolean, reason?:string, transferred?:boolean, legCount?:number,
 *   legs?:Array<{callId:string, agentId:string, agentName:string, agentLabel:string,
 *   parentId:string, status:string, ended:boolean, transcript:Array, functions:Array,
 *   invocationLog:object}>}>}
 */
export async function getTestCallData(agentId, listenerId, since) {
  if (!agentId && !listenerId) return { ok: false, reason: 'No agent to inspect.' };

  // 1. Discover the tested agent's leg (the seed) from its own call list. The
  //    widget exposes no call id, so this is matched by instanceId/time while the
  //    test is live.
  const seed = await findTestCall(agentId, listenerId, since);
  if (!seed) {
    return {
      ok: false,
      reason: 'No call was recorded — the browser call may not have connected, or the agent '
        + 'failed to start (check its model and voice options).',
    };
  }

  // 2. Expand to the whole linked tree via the durable parentId chain — this is
  //    what captures a transfer (the transferred-to agent runs on a child call
  //    under a different agentId). Fall back to just the seed if the lookup
  //    yields nothing.
  let legs = await waitForLinkedTree(seed.id);
  if (!legs.length) legs = [seed];

  legs = orderLegs(legs);
  const detailed = await Promise.all(legs.map((leg) => fetchLegData(leg, agentId)));
  return {
    ok: true,
    transferred: detailed.length > 1,
    legCount: detailed.length,
    legs: detailed,
  };
}

/**
 * Same diagnostics bundle as {@link getTestCallData}, but for an already-recorded
 * call whose id we already know (e.g. a row in the Observe list) — so we skip the
 * test-time discovery and expand the linked tree straight from that call id. A
 * historical call has already ended, so there's no need to poll for settle; the
 * per-leg fetch still retries once in case the final logs are a beat behind.
 *
 * @param {string} callId        any leg of the call (root or a transferred-to leg)
 * @param {string} [agentIdFallback] agent id from the list row, used when a leg
 *   record omits its own agentId
 * @returns same shape as getTestCallData: `{ ok, transferred, legCount, legs }`
 */
export async function getCallDiagnostics(callId, agentIdFallback) {
  if (!callId) return { ok: false, reason: 'No call to inspect.' };
  let legs = await listLinked(callId);
  if (!legs.length) legs = [{ id: callId, agentId: agentIdFallback || null }];
  legs = orderLegs(legs);
  const detailed = await Promise.all(legs.map((leg) => fetchLegData(leg, agentIdFallback)));
  return {
    ok: true,
    transferred: detailed.length > 1,
    legCount: detailed.length,
    legs: detailed,
  };
}
